import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Search, ArrowRight, MapPin, TrendingDown, TrendingUp,
  Phone, Info, SlidersHorizontal, AlertCircle, Loader2,
  Building2, Database, Bookmark, BookmarkCheck, X, Check, BarChart2,
  CheckCircle2, ExternalLink, LocateFixed, Calculator,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PriceDistributionChart from '../components/PriceDistributionChart';
import { useGeoState } from '../utils/geoState';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_COMPARE = 3;
const AMBER_FLASH_MS = 700;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
  'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

function getCardKey(r) {
  return `${r.provider_name}|${r.cpt_code ?? ''}|${r.cash_price ?? ''}`;
}

function buildIntentLabel(intent, raw) {
  if (!intent || Object.keys(intent).length === 0 || intent.error) {
    return `Showing all results for "${raw}"`;
  }
  const parts = [];
  const procs = (intent.procedure_keywords || []).join(', ');
  if (procs) parts.push(procs);
  if (intent.state) parts.push(`in ${intent.state}`);
  if (intent.max_price) parts.push(`under $${intent.max_price.toLocaleString()}`);
  return parts.length ? `Showing results for ${parts.join(' ')}` : `Showing all results for "${raw}"`;
}

function SavingsBadge({ result }) {
  if (!result) return null;
  const {
    is_lowest_nationally,
    is_lowest_in_state,
    cash_pay_only,
    data_source,
    savings_vs_regional,
    state,
  } = result;

  // 4a — Warm gold: nationally cheapest
  if (is_lowest_nationally) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 bg-amber-400 text-amber-900 border border-amber-500">
        🏆 Lowest National Price
      </span>
    );
  }

  // 4b — Light gold: cheapest in this state
  if (is_lowest_in_state) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-400">
        <Check size={10} /> Lowest in {state}
      </span>
    );
  }

  // 4c — Teal: informational cash-pay badge (not competitive)
  if (cash_pay_only && data_source === 'provider_directory') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200">
        Transparent Cash Pricing
      </span>
    );
  }

  if (!savings_vs_regional) return null;
  const isBelow = savings_vs_regional.includes('below');

  // 4d — Below regional avg: always show (green, positive signal)
  if (isBelow) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">
        <TrendingDown size={10} /> {savings_vs_regional}
      </span>
    );
  }

  // 4e — Above regional avg: only show for hospital price data (accurate context)
  if (data_source === 'price_transparency') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">
        <TrendingUp size={10} /> {savings_vs_regional}
      </span>
    );
  }

  // 4f — No badge
  return null;
}

function HospitalInfoModal({ result, onClose }) {
  if (!result) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
      data-testid="hospital-modal-overlay"
    >
      <div
        className="bg-white max-w-md w-full p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="hospital-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2
              className="text-lg font-semibold text-stone-900 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hospital-modal-title"
            >
              {result.provider_name}
            </h2>
            {(result.city || result.state) && (
              <p className="flex items-center gap-1 text-sm text-stone-500 mt-1">
                <MapPin size={12} />
                {[result.city, result.state].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-1.5 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors flex-shrink-0"
            data-testid="hospital-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        {result.cpt_code && (
          <p className="text-xs text-[#A8B4B8] font-mono mb-3">
            CPT {result.cpt_code} — {result.procedure_description}
          </p>
        )}

        <div className="bg-[#F0EEE9] border border-stone-200 p-4 mb-4">
          <p className="text-sm text-stone-700 leading-relaxed">
            This facility's pricing data is sourced from CMS-mandated price
            transparency files. Contact this hospital directly to confirm
            cash-pay pricing and availability.
          </p>
        </div>

        {result.cash_price != null && (
          <p className="text-xs text-stone-500 mb-4">
            Listed cash price: <strong className="text-[#0D3D4A]">${result.cash_price.toLocaleString()}</strong>
            {result.regional_average && (
              <> &nbsp;·&nbsp; Regional avg: ${result.regional_average.toLocaleString()}</>
            )}
          </p>
        )}

        <a
          href={`https://www.google.com/search?q=${encodeURIComponent((result.provider_name || '') + ' hospital price transparency')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-[#0D3D4A] border border-[#0D3D4A] px-4 py-2.5 hover:bg-[#0D3D4A] hover:text-white transition-colors"
          data-testid="hospital-modal-search-link"
        >
          <ExternalLink size={13} /> Search this hospital
        </a>
      </div>
    </div>
  );
}


function ResultCard({ result, isSelected, amberFlash, onToggle, onContact, onViewHospital, contacted, onAddToCalc }) {
  const isProvider = result.data_source === 'provider_directory';
  const isClickable = isProvider && result.provider_id;
  const testSlug = result.provider_name?.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, '');
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (isClickable) navigate(`/provider/${result.provider_id}`);
  };

  return (
    <div
      className={`bg-white border p-6 transition-all duration-150 flex flex-col relative
        ${amberFlash
          ? 'border-amber-400 ring-2 ring-amber-300/50 bg-amber-50/40'
          : isSelected
            ? 'border-[#0D3D4A] ring-2 ring-[#0D3D4A]/20'
            : isClickable
              ? 'border-stone-200 hover:border-[#0D3D4A] hover:shadow-md cursor-pointer'
              : 'border-stone-200 hover:border-[#0D3D4A]/30'
        }
      `}
      data-testid={`result-card-${testSlug}`}
      onClick={handleCardClick}
    >
      {/* Compare checkbox */}
      <button
        onClick={e => { e.stopPropagation(); onToggle(result); }}
        className={`absolute top-3 right-3 w-5 h-5 flex items-center justify-center border-2 rounded transition-colors flex-shrink-0
          ${isSelected
            ? 'bg-[#0D3D4A] border-[#0D3D4A]'
            : amberFlash
              ? 'bg-amber-100 border-amber-400'
              : 'bg-white border-stone-300 hover:border-[#0D3D4A]'
          }
        `}
        data-testid={`compare-checkbox-${testSlug}`}
        title={isSelected ? 'Remove from comparison' : 'Add to comparison'}
      >
        {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
      </button>

      <div className="flex items-start justify-between gap-3 mb-3 pr-8">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {isProvider ? (
              <Building2 size={13} className="text-[#0D3D4A] flex-shrink-0" />
            ) : (
              <Database size={13} className="text-stone-400 flex-shrink-0" />
            )}
            <span className="text-xs text-[#A8B4B8] font-medium uppercase tracking-wider">
              {isProvider ? 'Verified Provider' : 'Hospital Price Data'}
            </span>
            {result.cash_pay_only && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="text-emerald-500">$</span> Cash Pay Only
              </span>
            )}
          </div>
          <h3 className="font-semibold text-stone-900 text-base leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {result.provider_name}
          </h3>
          {(result.city || result.state) && (
            <p className="flex items-center gap-1 text-xs text-stone-500 mt-0.5">
              <MapPin size={10} />
              {[result.city, result.state].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
      </div>

      <p className="text-sm text-stone-600 mb-4 leading-relaxed line-clamp-2">
        {result.procedure_description}
        {result.cpt_code && (
          <span className="ml-1.5 text-xs text-[#A8B4B8] font-mono">CPT {result.cpt_code}</span>
        )}
      </p>

      <div className="mt-auto">
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-xs text-stone-500 mb-0.5 uppercase tracking-wider font-medium">Cash Price</p>
            <p className="text-3xl font-semibold text-[#0D3D4A]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="cash-price">
              {result.cash_price != null
                ? `$${result.cash_price.toLocaleString()}`
                : <span className="text-stone-400 text-lg">Not listed</span>
              }
            </p>
            {result.gross_charge != null && (
              <p className="text-xs text-stone-400 line-through mt-0.5">
                ${result.gross_charge.toLocaleString()} gross charge
              </p>
            )}
          </div>
          {result.regional_average && (
            <div className="text-right">
              <p className="text-xs text-stone-400">Regional avg</p>
              <p className="text-sm font-medium text-stone-500">
                ${result.regional_average.toLocaleString()}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <SavingsBadge result={result} />
          {isProvider ? (
            <button
              onClick={e => { e.stopPropagation(); onContact(result); }}
              disabled={contacted}
              className="text-xs font-semibold px-4 py-2 transition-colors flex items-center gap-1.5 disabled:cursor-default"
              style={contacted
                ? { backgroundColor: '#059669', color: 'white' }
                : { backgroundColor: '#0D3D4A', color: 'white' }
              }
              onMouseEnter={e => { if (!contacted) e.currentTarget.style.backgroundColor = '#0E4557'; }}
              onMouseLeave={e => { if (!contacted) e.currentTarget.style.backgroundColor = '#0D3D4A'; }}
              data-testid={`cta-btn-${testSlug}`}
            >
              {contacted
                ? <><CheckCircle2 size={11} /> Contacted</>
                : <><Phone size={11} /> Contact Provider</>
              }
            </button>
          ) : (
            <button
              onClick={e => { e.stopPropagation(); onViewHospital(result); }}
              className="text-xs font-semibold px-4 py-2 transition-colors flex items-center gap-1.5"
              style={{ backgroundColor: '#0D3D4A', color: 'white' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0E4557'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#0D3D4A'}
              data-testid={`cta-btn-${testSlug}`}
            >
              <Info size={11} /> View Hospital Info
            </button>
          )}
        </div>
        {/* Add to Cost Calculator */}
        {result.cash_price != null && (
          <button
            onClick={e => { e.stopPropagation(); onAddToCalc(result); }}
            className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-medium text-[#0D3D4A] border border-[#0D3D4A]/30 py-2 hover:bg-[#0D3D4A]/5 transition-colors"
            data-testid={`add-to-calc-btn-${testSlug}`}
          >
            <Calculator size={12} /> Add to Cost Calculator
          </button>
        )}
      </div>
    </div>
  );
}

function ComparePanel({ compareList, onClose }) {
  const TEAL_SHADES = ['#0D3D4A', '#1a5c6b', '#2a7a8c'];

  const chartData = {
    labels: compareList.map(r => {
      const n = r.provider_name || 'Unknown';
      return n.length > 20 ? n.slice(0, 17) + '…' : n;
    }),
    datasets: [{
      label: 'Cash Price ($)',
      data: compareList.map(r => r.cash_price || 0),
      backgroundColor: TEAL_SHADES.slice(0, compareList.length),
      borderRadius: 6,
      borderSkipped: false,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` $${Number(ctx.raw).toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (v) => `$${Number(v).toLocaleString()}`,
          font: { size: 11 },
        },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } },
      },
    },
  };

  const tableRows = [
    { label: 'Location',     fn: r => [r.city, r.state].filter(Boolean).join(', ') || '—' },
    { label: 'Procedure',    fn: r => r.procedure_description || '—' },
    { label: 'CPT Code',     fn: r => r.cpt_code ? <span className="font-mono text-xs">{r.cpt_code}</span> : '—' },
    {
      label: 'Cash Price',
      fn: r => r.cash_price != null
        ? <span className="font-bold text-[#0D3D4A] text-lg">${r.cash_price.toLocaleString()}</span>
        : <span className="text-stone-400">Not listed</span>,
    },
    {
      label: 'Regional Avg',
      fn: r => r.regional_average != null ? `$${r.regional_average.toLocaleString()}` : '—',
    },
    {
      label: 'vs. Region',
      fn: r => <SavingsBadge result={r} />,
    },
    {
      label: 'Source',
      fn: r => r.data_source === 'provider_directory'
        ? <span className="flex items-center gap-1 text-xs text-[#0D3D4A]"><Building2 size={11} /> Verified Provider</span>
        : <span className="flex items-center gap-1 text-xs text-stone-500"><Database size={11} /> Hospital Data</span>,
    },
  ];

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-[#0D3D4A] shadow-2xl max-h-[80vh] overflow-y-auto"
      data-testid="compare-panel"
    >
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <BarChart2 size={18} className="text-[#0D3D4A]" />
            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Comparing {compareList.length} Provider{compareList.length !== 1 ? 's' : ''}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-stone-100 transition-colors text-stone-500 hover:text-stone-800"
            data-testid="compare-panel-close"
            title="Close comparison"
          >
            <X size={18} />
          </button>
        </div>

        {/* Bar Chart */}
        <div className="mb-6 h-52 bg-[#F0EEE9] border border-stone-100 rounded-sm p-4">
          <Bar data={chartData} options={chartOptions} />
        </div>

        {/* Side-by-side Table */}
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="compare-table">
            <thead>
              <tr className="border-b-2 border-[#0D3D4A]/10">
                <th className="text-left py-3 pr-6 text-xs font-semibold text-stone-400 uppercase tracking-wider w-32">
                  Field
                </th>
                {compareList.map((r, i) => (
                  <th key={i} className="text-left py-3 px-4 text-sm font-semibold text-stone-900 min-w-[180px]"
                    style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {r.provider_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map(({ label, fn }) => (
                <tr key={label} className="border-b border-stone-100">
                  <td className="py-3 pr-6 text-xs text-stone-400 font-semibold uppercase tracking-wider align-top">
                    {label}
                  </td>
                  {compareList.map((r, i) => (
                    <td key={i} className="py-3 px-4 text-sm text-stone-700 align-top">
                      {fn(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white border border-stone-200 p-6 animate-pulse">
      <div className="h-3 bg-stone-200 rounded w-1/3 mb-3" />
      <div className="h-5 bg-stone-200 rounded w-3/4 mb-2" />
      <div className="h-3 bg-stone-200 rounded w-1/2 mb-4" />
      <div className="h-8 bg-stone-200 rounded w-1/3 mb-4" />
      <div className="h-8 bg-stone-200 rounded w-full" />
    </div>
  );
}

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, authHeaders } = useAuth();

  const rawQuery = searchParams.get('q') || '';
  const stateParam  = searchParams.get('state') || '';
  const [refineQuery, setRefineQuery] = useState(rawQuery);
  const [results, setResults] = useState([]);
  const [intent, setIntent] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fallback, setFallback] = useState(false);

  // Chart data
  const [distData, setDistData] = useState(null);
  const [showChart, setShowChart] = useState(true);

  // Save search
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showSavePopover, setShowSavePopover] = useState(false);
  const savePopoverRef = useRef(null);

  // Compare state
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [amberFlashId, setAmberFlashId] = useState(null);

  // Contact state
  const [contactedProviders, setContactedProviders] = useState(new Set());
  const [contactingId, setContactingId] = useState(null);
  const [hospitalModal, setHospitalModal] = useState(null);

  // Filters (client-side)
  const [stateFilter, setStateFilter] = useState(stateParam);
  const [maxPrice, setMaxPrice] = useState(50000);
  const [showProviders, setShowProviders] = useState(true);
  const [showPriceData, setShowPriceData] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('price_asc');
  const [originalResults, setOriginalResults] = useState([]);

  // Near Me geolocation
  const { loading: geoLoading, detect: detectLocation } = useGeoState(
    useCallback((state) => {
      setStateFilter(state);
    }, []),
  );

  // Clear amber flash after timeout
  useEffect(() => {
    if (!amberFlashId) return;
    const t = setTimeout(() => setAmberFlashId(null), AMBER_FLASH_MS);
    return () => clearTimeout(t);
  }, [amberFlashId]);

  const handleToggleCompare = useCallback((result) => {
    const key = getCardKey(result);
    setCompareList(prev => {
      const idx = prev.findIndex(r => getCardKey(r) === key);
      if (idx !== -1) return prev.filter((_, i) => i !== idx);
      if (prev.length < MAX_COMPARE) return [...prev, result];
      const oldest = prev[0];
      setAmberFlashId(getCardKey(oldest));
      return [...prev.slice(1), result];
    });
  }, []);

  const handleContact = useCallback(async (result) => {
    if (!token) {
      toast.error('Sign in to contact this provider.', {
        action: { label: 'Sign in', onClick: () => navigate('/auth') },
      });
      return;
    }
    const pid = result.provider_id;
    if (!pid || contactedProviders.has(pid) || contactingId === pid) return;
    setContactingId(pid);
    try {
      await axios.post(`${API}/providers/${pid}/contact`, {}, { headers: authHeaders });
      setContactedProviders(prev => new Set([...prev, pid]));
      toast.success('Your contact request has been sent. The provider will receive a notification.');
    } catch (e) {
      toast.error('Could not send contact request. Please try again.');
    } finally {
      setContactingId(null);
    }
  }, [token, authHeaders, contactedProviders, contactingId, navigate]);

  const handleViewHospital = useCallback((result) => {
    setHospitalModal(result);
  }, []);

  const handleAddToCalc = useCallback((result) => {
    try {
      const existing = JSON.parse(localStorage.getItem('meridian_cost_calc_facilities') || '[]');
      if (existing.length >= 4) {
        toast.error('Cost calculator already has 4 facilities. Open the calculator to remove one first.', {
          action: { label: 'Open Calculator', onClick: () => navigate('/total-cost') },
        });
        return;
      }
      const alreadyAdded = existing.some(f =>
        f.name === result.provider_name && f.city === result.city && f.state === result.state
      );
      if (alreadyAdded) {
        toast.info('This facility is already in your cost calculator.', {
          action: { label: 'View Calculator', onClick: () => navigate('/total-cost') },
        });
        return;
      }
      const newEntry = {
        name: result.provider_name || '',
        city: result.city || '',
        state: result.state || '',
        procedure_price: result.cash_price || 0,
        cash_price: result.cash_price || 0,
        cpt_code: result.cpt_code || '',
        procedure_description: result.procedure_description || '',
      };
      localStorage.setItem(
        'meridian_cost_calc_facilities',
        JSON.stringify([...existing, newEntry])
      );
      toast.success(`${result.provider_name} added to cost calculator.`, {
        action: {
          label: 'View Calculator',
          onClick: () => navigate('/total-cost?load=1'),
        },
      });
    } catch {
      toast.error('Could not add to calculator. Please try again.');
    }
  }, [navigate]);

  const runSearch = useCallback(async (query) => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setDistData(null);
    setIsSaved(false);
    setCompareList([]);
    setShowCompare(false);
    try {
      const res = await axios.post(`${API}/ai/search-intent`, { query });
      const newResults = res.data.results || [];
      const newIntent  = res.data.intent || {};
      setResults(newResults);
      setOriginalResults(newResults);
      setIntent(newIntent);
      setFallback(res.data.fallback || false);

      // Fetch distribution chart data
      const cpt = (newIntent.cpt_codes || [])[0];
      const st  = newIntent.state;
      if (cpt) {
        const distRes = await axios.get(`${API}/prices/distribution`, {
          params: { cpt, ...(st ? { state: st } : {}) },
        });
        setDistData(distRes.data);
      }

      // Check if search is already saved
      if (token) {
        try {
          const saved = await axios.get(`${API}/user/saved-searches`, { headers: authHeaders });
          const cptToCheck = (newIntent.cpt_codes || [])[0];
          setIsSaved((saved.data || []).some(
            s => s.procedure_cpt === cptToCheck && (!newIntent.state || s.home_state === newIntent.state)
          ));
        } catch (_) {}
      }
    } catch (e) {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    if (rawQuery) {
      setRefineQuery(rawQuery);
      runSearch(rawQuery);
    }
  }, [rawQuery, runSearch]);

  const handleRefine = (e) => {
    e.preventDefault();
    if (refineQuery.trim()) {
      setSearchParams({ q: refineQuery.trim() });
    }
  };

  // Close save popover on outside click
  useEffect(() => {
    if (!showSavePopover) return;
    const handle = (e) => {
      if (savePopoverRef.current && !savePopoverRef.current.contains(e.target)) {
        setShowSavePopover(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showSavePopover]);

  const handleSaveSearch = async () => {
    if (!token) {
      setShowSavePopover(true);
      return;
    }
    if (isSaved) return;
    setSaveLoading(true);
    try {
      const payload = {
        procedure_name: (intent.procedure_keywords || [])[0] || rawQuery,
        procedure_cpt:  (intent.cpt_codes || [])[0] || '',
        home_state:     intent.state || '',
        insurance_deductible: 0,
        insurance_oop_max:    0,
        already_paid:         0,
      };
      await axios.post(`${API}/user/saved-searches`, payload, { headers: authHeaders });
      setIsSaved(true);
      toast.success('Search saved to your dashboard.');
    } catch (e) {
      toast.error('Could not save search. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  // Client-side filtered results
  const filtered = results.filter(r => {
    if (stateFilter && r.state !== stateFilter) return false;
    if (r.cash_price != null && r.cash_price > maxPrice) return false;
    if (!showProviders && r.data_source === 'provider_directory') return false;
    if (!showPriceData && r.data_source === 'price_transparency') return false;
    if (categoryFilter && r.data_source === 'provider_directory' && r.category && r.category !== categoryFilter) return false;
    // Exclude sub-$500 price_transparency records — these are payer-negotiated rates
    // mistakenly stored in the cash_price field, not true cash/chargemaster prices
    if (r.data_source === 'price_transparency' && r.cash_price != null && r.cash_price < 500) return false;
    return true;
  });

  // Stats
  const uniqueStates = [...new Set(filtered.map(r => r.state).filter(Boolean))];
  const withPrices = filtered.filter(r => r.cash_price != null);
  const lowestResult = withPrices.length
    ? withPrices.reduce((a, b) => (a.cash_price < b.cash_price ? a : b))
    : null;
  const regionalAvg = (() => {
    const avgs = filtered.filter(r => r.regional_average != null).map(r => r.regional_average);
    return avgs.length ? Math.round(avgs.reduce((a, b) => a + b, 0) / avgs.length) : null;
  })();
  const mainProc = (intent?.procedure_keywords || [])[0];
  const intentLabel = buildIntentLabel(intent, rawQuery);
  const showFooter = compareList.length >= 1 && !showCompare;

  // Sort filtered results
  const sortedResults = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === 'price_asc')  return arr.sort((a, b) => (a.cash_price || 0) - (b.cash_price || 0));
    if (sortBy === 'price_desc') return arr.sort((a, b) => (b.cash_price || 0) - (a.cash_price || 0));
    if (sortBy === 'rating')     return arr.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    if (sortBy === 'best_match') {
      const filteredSet = new Set(filtered);
      return originalResults.filter(r => filteredSet.has(r));
    }
    return arr;
  }, [filtered, sortBy, originalResults]);

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Search Refinement Bar */}
      <div className="bg-[#0D3D4A] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <form onSubmit={handleRefine} className="flex gap-0 max-w-3xl" data-testid="refine-search-form">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8B4B8]" size={16} />
              <input
                type="text"
                value={refineQuery}
                onChange={e => setRefineQuery(e.target.value)}
                placeholder="Refine your search…"
                className="w-full pl-10 pr-4 py-3 bg-white text-sm text-stone-900 focus:outline-none focus:ring-0"
                data-testid="refine-search-input"
              />
            </div>
            <button
              type="submit"
              className="bg-white/10 border border-white/20 text-white px-5 py-3 text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2 whitespace-nowrap"
              data-testid="refine-search-submit"
            >
              Search <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </div>

      {/* Stats Bar */}
      {!loading && sortedResults.length > 0 && (
        <div className="bg-[#0D3D4A]/95 text-white/80 text-xs py-2.5 border-b border-white/10" data-testid="stats-bar">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-between gap-y-1">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
              <span><strong className="text-white">{sortedResults.length}</strong> results found across <strong className="text-white">{uniqueStates.length}</strong> state{uniqueStates.length !== 1 ? 's' : ''}</span>
              {regionalAvg && mainProc && (() => {
                // Guard against polluted regional avg: if any result has cash_price > $10k
                // but the avg is below $5k, it's likely payer-rate noise — show N/A instead
                const hasMajorSurgical = withPrices.some(r => (r.cash_price || 0) > 10_000);
                const avgIsSuspect = hasMajorSurgical && regionalAvg < 5_000;
                return (
                  <span>Regional avg for <strong className="text-white">{mainProc}</strong>:{' '}
                    <strong className="text-white">
                      {avgIsSuspect ? 'N/A' : `$${regionalAvg.toLocaleString()}`}
                    </strong>
                  </span>
                );
              })()}
              {lowestResult && (
                <span>Lowest: <strong className="text-white">${lowestResult.cash_price.toLocaleString()}</strong> at <strong className="text-white">{lowestResult.provider_name}</strong></span>
              )}
            </div>

            {/* Save search button */}
            <div className="relative flex-shrink-0" ref={savePopoverRef}>
              <button
                onClick={handleSaveSearch}
                disabled={saveLoading || isSaved}
                className="flex items-center gap-1.5 text-xs font-semibold text-white/80 hover:text-white transition-colors disabled:opacity-60 py-1"
                data-testid="save-search-btn"
              >
                {isSaved
                  ? <><BookmarkCheck size={13} className="text-emerald-400" /> Saved</>
                  : <><Bookmark size={13} /> Save this search</>
                }
              </button>
              {showSavePopover && (
                <div
                  className="absolute right-0 top-8 z-50 bg-white border border-stone-200 shadow-lg p-4 w-64"
                  data-testid="save-popover"
                >
                  <p className="text-xs text-stone-700 leading-relaxed mb-3">
                    Sign in or create a free account to save searches and receive price alerts.
                  </p>
                  <a
                    href="/auth"
                    className="block text-center bg-[#0D3D4A] text-white text-xs font-semibold py-2 hover:bg-[#0E4557] transition-colors"
                    data-testid="save-popover-signin-link"
                  >
                    Sign in / Create account
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Intent label + mobile filter toggle */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="intent-label">
              {loading ? 'Searching…' : intentLabel}
            </h1>
            {fallback && !loading && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertCircle size={11} /> Showing keyword results — AI could not extract a specific procedure
              </p>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden flex items-center gap-1.5 text-sm font-medium text-[#0D3D4A] bg-white border border-stone-200 px-3 py-2 flex-shrink-0"
            data-testid="mobile-filter-toggle"
          >
            <SlidersHorizontal size={14} /> Filters
          </button>
        </div>

        {/* Price Distribution Chart */}
        {!loading && distData && distData.buckets && distData.buckets.length > 0 && (
          <>
            <button
              className="lg:hidden w-full text-sm font-medium text-[#0D3D4A] bg-white border border-stone-200 px-4 py-2.5 mb-3 flex items-center justify-center gap-2"
              onClick={() => setShowChart(!showChart)}
              data-testid="show-chart-toggle"
            >
              {showChart ? 'Hide' : 'Show'} price distribution
            </button>
            <div className={`overflow-x-auto ${showChart ? 'mb-6' : 'hidden lg:mb-6 lg:block'}`}>
              {(() => {
                const procName = (intent.procedure_keywords || [])[0] || rawQuery;
                const chartTitle = intent.state
                  ? `Price distribution for ${procName} in ${intent.state} — ${distData.total_count} facilities`
                  : `National price distribution for ${procName} — ${distData.total_count} facilities`;
                const chartFootnote = intent.state
                  ? `Chart reflects verified price data for ${intent.state}. Prices may vary by facility and payer.`
                  : 'Chart reflects national price data across all states.';
                return (
                  <PriceDistributionChart
                    data={distData}
                    title={chartTitle}
                    footnote={chartFootnote}
                    defaultOpen={true}
                  />
                );
              })()}
            </div>
          </>
        )}

        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className={`w-56 flex-shrink-0 ${showFilters ? 'block' : 'hidden'} lg:block`} data-testid="filter-sidebar">
            <div className="bg-white border border-stone-200 p-5 space-y-6 sticky top-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold text-stone-900 uppercase tracking-wider">Filters</h2>
                <button
                  onClick={() => { setStateFilter(''); setMaxPrice(50000); setShowProviders(true); setShowPriceData(true); setCategoryFilter(''); }}
                  className="text-xs text-[#A8B4B8] hover:text-[#0D3D4A] transition-colors"
                  data-testid="clear-filters"
                >
                  Clear all
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">State</label>
                {/* Near Me button */}
                <button
                  type="button"
                  onClick={detectLocation}
                  disabled={geoLoading}
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-[#0D3D4A] border border-[#0D3D4A]/40 bg-white hover:bg-[#0D3D4A]/5 py-2 mb-2 transition-colors disabled:opacity-60"
                  data-testid="near-me-btn"
                >
                  {geoLoading
                    ? <><Loader2 size={11} className="animate-spin" /> Detecting location…</>
                    : <><LocateFixed size={11} /> Near me</>
                  }
                </button>
                <select
                  value={stateFilter}
                  onChange={e => setStateFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-[#A8B4B8] bg-[#F0EEE9] text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A]"
                  data-testid="state-filter"
                >
                  <option value="">All states</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
                  Max Price: <span className="text-[#0D3D4A]">${maxPrice.toLocaleString()}</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={50000}
                  step={500}
                  value={maxPrice}
                  onChange={e => setMaxPrice(Number(e.target.value))}
                  className="w-full accent-[#0D3D4A]"
                  data-testid="max-price-slider"
                />
                <div className="flex justify-between text-xs text-[#A8B4B8] mt-1">
                  <span>$0</span><span>$50k</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">Category</label>
                <select
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  className="w-full border border-stone-200 px-2 py-1.5 text-sm text-stone-700 focus:outline-none focus:border-[#0D3D4A]"
                  data-testid="filter-category"
                >
                  <option value="">All categories</option>
                  <option value="surgical">Surgical</option>
                  <option value="elective">Elective / Cosmetic</option>
                  <option value="dental">Dental</option>
                  <option value="fertility">Fertility</option>
                  <option value="medspa">Med Spa</option>
                  <option value="bariatric">Bariatric</option>
                  <option value="vision">Vision / Lasik</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">Data Source</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showProviders}
                      onChange={e => setShowProviders(e.target.checked)}
                      className="accent-[#0D3D4A]"
                      data-testid="filter-providers"
                    />
                    <span className="text-sm text-stone-700 flex items-center gap-1.5">
                      <Building2 size={12} className="text-[#0D3D4A]" /> Verified Providers
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showPriceData}
                      onChange={e => setShowPriceData(e.target.checked)}
                      className="accent-[#0D3D4A]"
                      data-testid="filter-price-data"
                    />
                    <span className="text-sm text-stone-700 flex items-center gap-1.5">
                      <Database size={12} className="text-stone-400" /> Hospital Price Data
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </aside>

          {/* Results */}
          <main className="flex-1 min-w-0">
            {loading && (
              <div>
                <div className="flex items-center gap-2 text-sm text-[#0D3D4A] mb-6" data-testid="loading-indicator">
                  <Loader2 size={16} className="animate-spin" />
                  AI is interpreting your query and finding the best prices…
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
                </div>
              </div>
            )}

            {!loading && error && (
              <div className="bg-red-50 border border-red-200 p-6 text-sm text-red-700 flex items-center gap-2" data-testid="search-error">
                <AlertCircle size={16} /> {error}
              </div>
            )}

            {!loading && !error && sortedResults.length === 0 && rawQuery && (
              <div className="bg-white border border-stone-200 p-12 text-center" data-testid="no-results">
                <Search size={32} className="text-[#A8B4B8] mx-auto mb-4" />
                <h3 className="font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No results found</h3>
                <p className="text-sm text-stone-500 mb-4">
                  {stateFilter || maxPrice < 50000
                    ? 'Try adjusting your filters, or broaden your search.'
                    : 'Try a different search — we may not have pricing data for this procedure yet.'}
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="text-sm font-medium text-[#0D3D4A] hover:underline"
                >
                  Back to home
                </button>
              </div>
            )}

            {!loading && !error && sortedResults.length > 0 && (
              <>
                {/* Sort bar */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-stone-500">{sortedResults.length} result{sortedResults.length !== 1 ? 's' : ''}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-stone-400">Sort:</span>
                    <select
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value)}
                      className="border border-stone-200 px-3 py-1.5 text-sm text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#0D3D4A]"
                      data-testid="sort-select"
                    >
                      <option value="price_asc">Price: Low to High</option>
                      <option value="price_desc">Price: High to Low</option>
                      <option value="best_match">Best Match</option>
                      <option value="rating">Rating</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="results-grid">
                  {sortedResults.map((r, i) => {
                    const key = getCardKey(r);
                    return (
                      <ResultCard
                        key={i}
                        result={r}
                        isSelected={compareList.some(c => getCardKey(c) === key)}
                        amberFlash={amberFlashId === key}
                        onToggle={handleToggleCompare}
                        onContact={handleContact}
                        onViewHospital={handleViewHospital}
                        onAddToCalc={handleAddToCalc}
                        contacted={contactedProviders.has(r.provider_id)}
                      />
                    );
                  })}
                </div>
              </>
            )}

            {/* Bottom padding so content isn't hidden behind sticky footer */}
            {showFooter && <div className="h-20" />}
          </main>
        </div>
      </div>

      {/* Sticky Compare Footer */}
      {showFooter && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-[#0D3D4A] shadow-2xl border-t border-white/10"
          data-testid="compare-footer"
        >
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {compareList.map((r, i) => (
                <span
                  key={i}
                  className="text-xs text-white/80 bg-white/10 px-2 py-1 rounded truncate max-w-[140px]"
                >
                  {r.provider_name}
                </span>
              ))}
              {compareList.length < MAX_COMPARE && (
                <span className="text-xs text-white/40 flex-shrink-0">
                  + {MAX_COMPARE - compareList.length} more
                </span>
              )}
            </div>
            <button
              onClick={() => setShowCompare(true)}
              disabled={compareList.length < 2}
              className="flex items-center gap-2 bg-white text-[#0D3D4A] text-sm font-semibold px-5 py-2 rounded-sm transition-opacity disabled:opacity-40 flex-shrink-0 hover:bg-stone-100"
              data-testid="compare-button"
            >
              <BarChart2 size={14} /> Compare selected ({compareList.length})
            </button>
          </div>
        </div>
      )}

      {/* Compare Panel */}
      {showCompare && (
        <ComparePanel
          compareList={compareList}
          onClose={() => setShowCompare(false)}
        />
      )}

      {/* Hospital Info Modal */}
      {hospitalModal && (
        <HospitalInfoModal
          result={hospitalModal}
          onClose={() => setHospitalModal(null)}
        />
      )}
    </div>
  );
}
